import React from 'react';
import styled from 'styled-components';
import { Search } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from './DropdownMenu';

// Styled Components
const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const TabsAndActionsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 4px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0;
  }
`;

const SearchAndSecondaryActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
`;

const SecondaryActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
  }
`;

const PrimaryActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 768px) {
    display: none; /* Hide on large screens - will be shown inline instead */
  }
  
  /* Make primary buttons wider on small screens */
  button {
    flex: 1;
    min-width: 200px;
  }
`;

const AllActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
  }
`;

const PrimaryButtonsInline = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 2px;
  gap: 0;
  width: 100%;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }
  
  @media (min-width: 768px) {
    width: auto;
  }
`;

const TabItem = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 8px 12px;
  border: none;
  background: ${props => props.$active ? props.theme.muted : 'transparent'};
  color: ${props => props.$active ? props.theme.foreground : props.theme.mutedForeground};
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  white-space: nowrap; /* Prevent text wrapping */
  
  &:hover {
    background: ${props => props.$active ? props.theme.muted : props.theme.muted}20;
  }
  
  &:focus-visible {
    outline: none !important;
  }
  
  &:focus {
    outline: none !important;
    border: none !important;
  }
  
  &:active {
    outline: none !important;
    border: none !important;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? props.theme.primary : 'transparent'};
    border-radius: 1px;
  }
  
  @media (max-width: 768px) {
    flex: none; /* Remove flex: 1 on mobile to allow scrolling */
    min-width: max-content; /* Ensure tabs don't shrink too much */
    padding: 8px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 11px;
  }
`;

const SearchContainer = styled.div<{ $compact?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  /* Stacked layout (< md): flex axis is vertical — avoid flex-basis/flex-grow here or
     the search row gets a bogus height and the icon centers in a huge gap. */
  flex: none;

  @media (min-width: 768px) {
    flex: ${(p) => (p.$compact ? '0 1 220px' : '1 1 auto')};
    width: ${(p) => (p.$compact ? '220px' : '320px')};
    max-width: ${(p) => (p.$compact ? '260px' : '360px')};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.mutedForeground};
  width: 16px;
  height: 16px;
  pointer-events: none;
`;

const SearchInput = styled(Input)`
  width: 100%;
  padding-left: 40px;
  height: 36px;
  font-size: 14px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.card};
  transition: all 0.2s ease;
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}20;
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.mutedForeground};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
  }
`;

// Types
export interface Tab {
  value: string;
  label: string;
  count?: number;
}

export interface ActionButton {
  label: string;
  icon: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'success' | 'premium';
  size?: 'sm' | 'md' | 'lg';
  onClick: () => void;
  disabled?: boolean;
  dropdown?: React.ReactNode;
}

export interface PageHeaderProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (value: string) => void;
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  actionButtons: ActionButton[];
  showSearch?: boolean;
  showTabs?: boolean;
  /** Narrower search field when multiple filters sit beside it (e.g. property + type). */
  compactSearch?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  tabs,
  activeTab,
  onTabChange,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  actionButtons,
  showSearch = true,
  showTabs = true,
  compactSearch = false,
}) => {
  // Separate primary and secondary action buttons
  const primaryButtons = actionButtons.filter(button => button.variant === 'primary');
  const secondaryButtons = actionButtons.filter(button => button.variant !== 'primary');

  return (
    <HeaderContainer>
      <TabsAndActionsRow>
        {showTabs && (
          <TabsContainer>
            {tabs.map((tab) => (
              <TabItem
                key={tab.value}
                $active={activeTab === tab.value}
                onClick={() => onTabChange(tab.value)}
              >
                {tab.label}
                {tab.count !== undefined && activeTab === tab.value && ` (${tab.count})`}
              </TabItem>
            ))}
          </TabsContainer>
        )}
        
        <SearchAndSecondaryActions>
          {showSearch && (
            <SearchContainer $compact={compactSearch}>
              <SearchIcon />
              <SearchInput
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
              />
            </SearchContainer>
          )}
          
          <AllActionButtons>
            {secondaryButtons.map((button, index) => (
              button.dropdown ? (
                <div key={index}>
                  {button.dropdown}
                </div>
              ) : (
                <Button
                  key={index}
                  variant={button.variant || 'outline'}
                  size={button.size || 'sm'}
                  onClick={button.onClick}
                  disabled={button.disabled}
                >
                  {button.icon}
                  <span style={{ marginLeft: '8px' }}>{button.label}</span>
                </Button>
              )
            ))}
            
            {/* Show primary buttons inline on large screens */}
            <PrimaryButtonsInline>
              {primaryButtons.map((button, index) => (
                <Button
                  key={`primary-${index}`}
                  variant={button.variant || 'primary'}
                  size={button.size || 'md'}
                  onClick={button.onClick}
                  disabled={button.disabled}
                >
                  {button.icon}
                  <span style={{ marginLeft: '8px' }}>{button.label}</span>
                </Button>
              ))}
            </PrimaryButtonsInline>
          </AllActionButtons>
        </SearchAndSecondaryActions>
      </TabsAndActionsRow>
      
      {/* Show primary buttons on separate line only on small screens */}
      {primaryButtons.length > 0 && (
        <PrimaryActionButtons>
          {primaryButtons.map((button, index) => (
            <Button
              key={index}
              variant={button.variant || 'primary'}
              size={button.size || 'md'}
              onClick={button.onClick}
              disabled={button.disabled}
            >
              {button.icon}
              <span style={{ marginLeft: '8px' }}>{button.label}</span>
            </Button>
          ))}
        </PrimaryActionButtons>
      )}
    </HeaderContainer>
  );
};

export default PageHeader;
